import React, { useState } from 'react';
import api from '../utils/api';

// Enhanced logging configuration
const ENABLE_DETAILED_LOGGING = process.env.REACT_APP_ENABLE_DETAILED_LOGGING === 'true';

// Enhanced logging functions
const logInfo = (message, context = null) => {
  console.log(`[FRONTEND-INFO] ${message}`, context ? JSON.stringify(context, null, 2) : '');
};

const logError = (message, error = null, context = null) => {
  console.error(`[FRONTEND-ERROR] ${message}`, error, context ? JSON.stringify(context, null, 2) : '');
};

const logDebug = (message, context = null) => {
  if (ENABLE_DETAILED_LOGGING) {
    console.debug(`[FRONTEND-DEBUG] ${message}`, context ? JSON.stringify(context, null, 2) : '');
  }
};

const logNetworkRequest = (method, url, data = null) => {
  logDebug(`🌐 Frontend Request: ${method} ${url}`, {
    dataSize: data ? JSON.stringify(data).length : 0,
    timestamp: new Date().toISOString()
  });
};

const logNetworkResponse = (method, url, status, responseTime, error = null) => {
  if (error) {
    logError(`🌐 Frontend Network Error: ${method} ${url}`, error, { status, responseTime });
  } else {
    logDebug(`🌐 Frontend Network Response: ${method} ${url}`, { status, responseTime });
  }
};

// Always use KnowledgeBase (migration complete)

const CompanyInput = ({ onCompanySelected }) => {
  const [ticker, setTicker] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  const [useCustomDateRange, setUseCustomDateRange] = useState(false);
  


  // New KnowledgeBase processing function
  const processWithKnowledgeBase = async (ticker, startDate, endDate) => {
    logInfo(`🚀 Starting KnowledgeBase processing for ${ticker}`);
    
    // Step 1: Resolve ticker
    setProgress(10);
    setCurrentStep('Resolving ticker to company...');
    
    const tickerResponse = await api.get(`/api/knowledge-base/lookup?ticker=${ticker.toUpperCase()}`, {
      timeout: 900000 // 15 minutes timeout for KB operations
    });
    const companyName = tickerResponse.data.name;
    const companyCik = tickerResponse.data.cik;
    
    logInfo(`✅ Resolved ${ticker} to ${companyName} (CIK: ${companyCik})`);
    
    // Step 2: Process company filings (delta check + download + parse + sync)
    setProgress(20);
    setCurrentStep('Processing company filings...');
    
    const queryParams = new URLSearchParams({
      ticker: ticker.toUpperCase(),
      startDate: startDate || '2019-01-01',
      endDate: endDate || new Date().toISOString().split('T')[0]
    });
    
    logNetworkRequest('POST', `/api/knowledge-base/process-company?${queryParams.toString()}`);
    
    const processResponse = await api.post(`/api/knowledge-base/process-company?${queryParams.toString()}`, {}, {
      timeout: 900000 // 15 minutes timeout for KB operations
    });
    
    logNetworkResponse('POST', `/api/knowledge-base/process-company`, processResponse.status, 0);
    
    if (!processResponse.data.success) {
      throw new Error(processResponse.data.message || 'Processing failed');
    }
    
    const processingId = processResponse.data.processingId;
    const syncJobId = processResponse.data.syncJobId;
    const newFilings = processResponse.data.newFilings;
    
    if (!processingId) {
      // No new filings to process, go directly to chat
      setProgress(100);
      setCurrentStep('No new filings found! Loading chat interface...');
      
      setTimeout(() => {
        const selectedDateRange = useCustomDateRange && dateRange.start && dateRange.end ? dateRange : null;
        onCompanySelected(ticker.toUpperCase(), companyName, selectedDateRange);
      }, 1000);
      return;
    }
    
    logInfo(`✅ Started processing with ID: ${processingId}, Sync Job: ${syncJobId}, New filings: ${newFilings}`);
    
    // Step 3: Poll processing status
    setCurrentStep('Processing filings with AI...');
    await pollKnowledgeBaseStatus(processingId, syncJobId, setProgress, setCurrentStep, ticker, companyName);
    
    // Step 4: Complete (this will only run if polling didn't complete the process)
    setTimeout(() => {
      const selectedDateRange = useCustomDateRange && dateRange.start && dateRange.end ? dateRange : null;
      onCompanySelected(ticker.toUpperCase(), companyName, selectedDateRange);
    }, 1000);
  };

  // Enhanced status polling for KnowledgeBase
  const pollKnowledgeBaseStatus = async (processingId, syncJobId, setProgress, setCurrentStep, ticker, companyName) => {
    const pollInterval = 5000; // Poll every 5 seconds (increased from 2s)
    const maxPollTime = 30 * 60 * 1000; // Max 30 minutes
    const startTime = Date.now();
    
    logInfo(`🚀 Starting KnowledgeBase status polling`, {
      processingId,
      syncJobId,
      pollInterval: pollInterval / 1000 + ' seconds',
      maxPollTime: maxPollTime / 1000 / 60 + ' minutes'
    });
    
    let pollCount = 0;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 3; // Reduced from 5 to 3
    
    while (Date.now() - startTime < maxPollTime) {
      pollCount++;
      
      try {
        // Poll processing status with retry logic
        const statusResponse = await api.get(`/api/knowledge-base/status/${processingId}`, {
          timeout: 120000 // 2 minutes timeout
        });
        
        const status = statusResponse.data;
        logInfo(`✅ Processing status poll #${pollCount}`, {
          status: status.status,
          progress: status.progress?.percentage || 0,
          step: status.progress?.step
        });
        
        // Update progress
        const baseProgress = 20; // Starting from 20%
        const processingProgress = Math.min(status.progress.percentage, 90); // Cap at 90%
        const totalProgress = baseProgress + (processingProgress * 0.7); // 20% to 83%
        
        setProgress(Math.round(totalProgress));
        setCurrentStep(`${status.progress.step} (${status.progress.current}/${status.progress.total})`);
        
        if (status.status === 'completed') {
          logInfo(`✅ Processing completed, checking KB sync status`);
          
          // If we have a sync job ID, check its status
          if (syncJobId) {
            setCurrentStep('Syncing to Knowledge Base...');
            
            // Poll KB sync status with retry logic
            const syncStatusResponse = await api.get(`/api/knowledge-base/sync-status/${syncJobId}`, {
              timeout: 120000 // 2 minutes timeout
            });
            const syncStatus = syncStatusResponse.data;
            
            logInfo(`📊 KB Sync status: ${syncStatus.status}`);
            
            if (syncStatus.status === 'COMPLETE' || syncStatus.status === 'COMPLETED') {
              logInfo(`✅ KB sync completed successfully`);
              setProgress(100);
              setCurrentStep('Complete! Loading chat interface...');
              
              // Navigate to chat interface
              setTimeout(() => {
                const selectedDateRange = useCustomDateRange && dateRange.start && dateRange.end ? dateRange : null;
                onCompanySelected(ticker.toUpperCase(), companyName, selectedDateRange);
              }, 1000);
              return; // Exit the entire polling function
            } else if (syncStatus.status === 'FAILED') {
              throw new Error(`KB sync failed: ${syncStatus.failure_reason}`);
            } else {
              // Continue polling sync status
              setCurrentStep(`KB Sync: ${syncStatus.status}`);
              await new Promise(resolve => setTimeout(resolve, pollInterval));
              continue; // Continue the main while loop
            }
          } else {
            // No sync job, processing is complete
            setProgress(100);
            setCurrentStep('Complete! Loading chat interface...');
            
            // Navigate to chat interface
            setTimeout(() => {
              const selectedDateRange = useCustomDateRange && dateRange.start && dateRange.end ? dateRange : null;
              onCompanySelected(ticker.toUpperCase(), companyName, selectedDateRange);
            }, 1000);
            return; // Exit the entire polling function
          }
        } else if (status.status === 'failed') {
          throw new Error(`Processing failed: ${status.errors.join(', ')}`);
        }
        
        consecutiveErrors = 0; // Reset error counter on success
        
        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, pollInterval));
        
      } catch (error) {
        consecutiveErrors++;
        logError(`❌ Status poll #${pollCount} failed`, error);
        
        if (consecutiveErrors >= maxConsecutiveErrors) {
          throw error;
        }
        
        // Exponential backoff on error
        const backoffDelay = pollInterval * Math.pow(2, consecutiveErrors - 1);
        logInfo(`⏳ Retrying in ${backoffDelay / 1000} seconds (attempt ${consecutiveErrors}/${maxConsecutiveErrors})`);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
      }
    }
    
    throw new Error('Processing timed out after 30 minutes');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ticker.trim()) return;

    setIsLoading(true);
    setError('');
    setProgress(0);
    setCurrentStep('Starting analysis...');

    try {
      // Use KnowledgeBase processing
      logInfo(`🚀 Using KnowledgeBase processing for ${ticker}`);
      await processWithKnowledgeBase(ticker, dateRange.start, dateRange.end);
      return;

    } catch (err) {
      console.error('❌ [FRONTEND] Error processing company:', err);
      console.error('🔍 [FRONTEND] Full error object:', {
        message: err.message,
        code: err.code,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        config: err.config
      });
      setError(err.response?.data?.message || err.message || 'An error occurred');
      setIsLoading(false);
      setProgress(0);
      setCurrentStep('');
    }
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-card">
          <h2 className="loading-title">Processing {ticker.toUpperCase()}</h2>
          <p className="loading-subtitle">
            {currentStep.includes('Found existing data') 
              ? 'Found existing data! Skipping processing...' 
              : 'Downloading and analyzing SEC filings...'
            }
          </p>
          
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          
          <p className="progress-text">{currentStep}</p>
          <p className="progress-percentage">{progress}%</p>
          
          {currentStep.includes('Found existing data') && (
            <div style={{ 
              marginTop: '15px', 
              padding: '10px', 
              backgroundColor: '#e8f5e8', 
              border: '1px solid #4caf50', 
              borderRadius: '5px',
              fontSize: '0.9rem',
              color: '#2e7d32'
            }}>
              ✅ Data already exists! You can start chatting immediately.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="company-input-container">
      <div className="company-input-card">
        <h1 className="company-input-title">FundLens Chat</h1>
        <p className="company-input-subtitle">
          Enter a company ticker to start analyzing their SEC filings
        </p>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label htmlFor="ticker" className="input-label">
              Company Ticker Symbol
            </label>
            <input
              id="ticker"
              type="text"
              className="ticker-input"
              placeholder="e.g., AAPL, MSFT, BAC"
              value={ticker}
              onChange={(e) => setTicker(e.target.value.toUpperCase())}
              disabled={isLoading}
              required
            />
          </div>
          
          <div className="input-group">
            <label className="input-label">
              Analysis Time Period
            </label>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                <input
                  type="radio"
                  name="dateRange"
                  checked={!useCustomDateRange}
                  onChange={() => setUseCustomDateRange(false)}
                  disabled={isLoading}
                  style={{ marginRight: '8px' }}
                />
                <span>Last 5 years (default)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="radio"
                  name="dateRange"
                  checked={useCustomDateRange}
                  onChange={() => setUseCustomDateRange(true)}
                  disabled={isLoading}
                  style={{ marginRight: '8px' }}
                />
                <span>Custom date range</span>
              </label>
            </div>
            
            {useCustomDateRange && (
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="startDate" style={{ fontSize: '0.9rem', color: '#666', display: 'block', marginBottom: '4px' }}>
                    Start Date
                  </label>
                  <input
                    id="startDate"
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="endDate" style={{ fontSize: '0.9rem', color: '#666', display: 'block', marginBottom: '4px' }}>
                    End Date
                  </label>
                  <input
                    id="endDate"
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          
          
          <button 
            type="submit" 
            className="submit-button"
            disabled={isLoading || !ticker.trim()}
          >
            {isLoading ? 'Processing...' : 'Start Analysis'}
          </button>
        </form>
        
        <div style={{ marginTop: '20px', fontSize: '0.9rem', color: '#666' }}>
          <p>This will download and analyze:</p>
          <ul style={{ textAlign: 'left', marginTop: '8px' }}>
            <li>10-K Annual Reports</li>
            <li>10-Q Quarterly Reports</li>
            <li>8-K Current Reports</li>
          </ul>
          <p style={{ marginTop: '10px', fontStyle: 'italic' }}>
            💡 <strong>Tip:</strong> Use custom date range to focus on specific time periods for more targeted analysis.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompanyInput;
